<template>
    <Card>
        <template #title>
            Dados Bancários (Bradesco)
        </template>
        <template #content>
            <p class="text-sm text-gray-600 mb-3">
                Configure os dados do convênio Bradesco usados na geração de arquivos de remessa CNAB 240.
                Todos os campos são obrigatórios. A geração de remessa fica bloqueada enquanto o cadastro estiver
                incompleto.
            </p>

            <div v-if="loading" class="py-4 flex justify-content-center">
                <ProgressSpinner style="width: 42px; height: 42px" strokeWidth="4" />
            </div>

            <div v-else class="flex flex-column gap-3" style="max-width: 48rem">
                <Message
                    v-if="statusCompleto"
                    severity="success"
                    :closable="false"
                >
                    Dados bancários completos. A geração de remessa CNAB 240 está liberada.
                </Message>

                <Message
                    v-else
                    severity="warn"
                    :closable="false"
                >
                    <div>
                        <strong class="block mb-1">Cadastro incompleto.</strong>
                        <span v-if="camposFaltantesLabels.length">
                            Preencha: {{ camposFaltantesLabels.join(', ') }}.
                        </span>
                        <span v-else>
                            Preencha os campos abaixo para liberar a geração de remessa.
                        </span>
                    </div>
                </Message>

                <form class="grid formgrid align-items-end" @submit.prevent="onSubmit">
                    <div class="field col-12 md:col-3">
                        <label for="db-banco" class="block mb-1 font-medium">Banco</label>
                        <InputText
                            id="db-banco"
                            :value="bancoLabel"
                            class="w-full"
                            readonly
                            disabled
                            aria-readonly="true"
                        />
                        <small class="text-gray-500">Fixado em Bradesco (código 237).</small>
                    </div>

                    <div class="field col-8 md:col-3">
                        <label :for="agenciaId" class="block mb-1 font-medium">
                            Agência<span class="text-red-500"> *</span>
                        </label>
                        <InputText
                            :id="agenciaId"
                            v-model="form.agencia"
                            class="w-full"
                            maxlength="5"
                            inputmode="numeric"
                            :aria-invalid="!!errors.agencia"
                            :aria-describedby="errors.agencia ? `${agenciaId}-error` : undefined"
                            @input="onNumericInput('agencia', $event, 5)"
                        />
                        <small
                            v-if="errors.agencia"
                            :id="`${agenciaId}-error`"
                            class="text-red-500 text-xs"
                        >
                            {{ errors.agencia }}
                        </small>
                    </div>

                    <div class="field col-4 md:col-3">
                        <label :for="agenciaDvId" class="block mb-1 font-medium" style="white-space: nowrap;">
                            DV da agência<span class="text-red-500"> *</span>
                        </label>
                        <InputText
                            :id="agenciaDvId"
                            v-model="form.agencia_dv"
                            class="w-full"
                            maxlength="1"
                            :aria-invalid="!!errors.agencia_dv"
                            :aria-describedby="errors.agencia_dv ? `${agenciaDvId}-error` : undefined"
                            @input="onAlphaNumDvInput('agencia_dv', $event)"
                        />
                        <small
                            v-if="errors.agencia_dv"
                            :id="`${agenciaDvId}-error`"
                            class="text-red-500 text-xs"
                        >
                            {{ errors.agencia_dv }}
                        </small>
                    </div>

                    <div class="field col-8 md:col-3">
                        <label :for="contaId" class="block mb-1 font-medium">
                            Conta<span class="text-red-500"> *</span>
                        </label>
                        <InputText
                            :id="contaId"
                            v-model="form.conta"
                            class="w-full"
                            maxlength="12"
                            inputmode="numeric"
                            :aria-invalid="!!errors.conta"
                            :aria-describedby="errors.conta ? `${contaId}-error` : undefined"
                            @input="onNumericInput('conta', $event, 12)"
                        />
                        <small
                            v-if="errors.conta"
                            :id="`${contaId}-error`"
                            class="text-red-500 text-xs"
                        >
                            {{ errors.conta }}
                        </small>
                    </div>

                    <div class="field col-4 md:col-3">
                        <label :for="contaDvId" class="block mb-1 font-medium" style="white-space: nowrap;">
                            DV da conta<span class="text-red-500"> *</span>
                        </label>
                        <InputText
                            :id="contaDvId"
                            v-model="form.conta_dv"
                            class="w-full"
                            maxlength="1"
                            :aria-invalid="!!errors.conta_dv"
                            :aria-describedby="errors.conta_dv ? `${contaDvId}-error` : undefined"
                            @input="onAlphaNumDvInput('conta_dv', $event)"
                        />
                        <small
                            v-if="errors.conta_dv"
                            :id="`${contaDvId}-error`"
                            class="text-red-500 text-xs"
                        >
                            {{ errors.conta_dv }}
                        </small>
                    </div>

                    <div class="field col-12 md:col-9">
                        <label :for="convenioId" class="block mb-1 font-medium">
                            Nº do convênio<span class="text-red-500"> *</span>
                        </label>
                        <InputText
                            :id="convenioId"
                            v-model="form.convenio"
                            class="w-full"
                            maxlength="20"
                            inputmode="numeric"
                            autocomplete="off"
                            :aria-invalid="!!errors.convenio"
                            :aria-describedby="errors.convenio ? `${convenioId}-error` : undefined"
                            @input="onNumericInput('convenio', $event, 20)"
                        />
                        <small
                            v-if="errors.convenio"
                            :id="`${convenioId}-error`"
                            class="text-red-500 text-xs"
                        >
                            {{ errors.convenio }}
                        </small>
                    </div>

                    <div class="field col-12 md:col-6">
                        <label :for="codigoCedenteId" class="block mb-1 font-medium">
                            Código do cedente<span class="text-red-500"> *</span>
                        </label>
                        <InputText
                            :id="codigoCedenteId"
                            v-model="form.codigo_cedente"
                            class="w-full"
                            maxlength="20"
                            inputmode="numeric"
                            autocomplete="off"
                            :aria-invalid="!!errors.codigo_cedente"
                            :aria-describedby="
                                errors.codigo_cedente ? `${codigoCedenteId}-error` : undefined
                            "
                            @input="onNumericInput('codigo_cedente', $event, 20)"
                        />
                        <small
                            v-if="errors.codigo_cedente"
                            :id="`${codigoCedenteId}-error`"
                            class="text-red-500 text-xs"
                        >
                            {{ errors.codigo_cedente }}
                        </small>
                    </div>

                    <div class="field col-12 md:col-6">
                        <label :for="cnpjId" class="block mb-1 font-medium">
                            CNPJ<span class="text-red-500"> *</span>
                        </label>
                        <InputMask
                            :id="cnpjId"
                            v-model="form.cnpj"
                            mask="99.999.999/9999-99"
                            class="w-full"
                            autocomplete="off"
                            :aria-invalid="!!errors.cnpj"
                            :aria-describedby="errors.cnpj ? `${cnpjId}-error` : undefined"
                        />
                        <small
                            v-if="errors.cnpj"
                            :id="`${cnpjId}-error`"
                            class="text-red-500 text-xs"
                        >
                            {{ errors.cnpj }}
                        </small>
                    </div>

                    <div class="field col-12 md:col-3">
                        <label :for="seqInicialId" class="block mb-1 font-medium">
                            Sequencial inicial<span class="text-red-500"> *</span>
                        </label>
                        <InputText
                            :id="seqInicialId"
                            v-model="form.sequencial_inicial"
                            class="w-full"
                            maxlength="6"
                            inputmode="numeric"
                            :aria-invalid="!!errors.sequencial_inicial"
                            :aria-describedby="
                                errors.sequencial_inicial ? `${seqInicialId}-error` : undefined
                            "
                            @input="onNumericInput('sequencial_inicial', $event, 6)"
                        />
                        <small
                            v-if="errors.sequencial_inicial"
                            :id="`${seqInicialId}-error`"
                            class="text-red-500 text-xs"
                        >
                            {{ errors.sequencial_inicial }}
                        </small>
                        <small v-else class="text-gray-500">
                            Valor inicial usado na primeira remessa.
                        </small>
                    </div>

                    <div class="field col-12 md:col-3">
                        <label :for="seqAtualId" class="block mb-1 font-medium">Sequencial atual</label>
                        <InputText
                            :id="seqAtualId"
                            :value="form.sequencial_atual || '—'"
                            class="w-full"
                            readonly
                            disabled
                            aria-readonly="true"
                        />
                        <small class="text-gray-500">
                            Atualizado automaticamente a cada remessa gerada.
                        </small>
                    </div>

                    <div class="col-12 mt-2 flex flex-wrap gap-2">
                        <Button
                            type="submit"
                            label="Salvar"
                            icon="pi pi-check"
                            severity="info"
                            :loading="saving"
                            :disabled="!isDirty || saving"
                        />
                        <Button
                            type="button"
                            label="Descartar alterações"
                            icon="pi pi-undo"
                            severity="secondary"
                            outlined
                            :disabled="!isDirty || saving"
                            @click="resetForm"
                        />
                    </div>
                </form>
            </div>
        </template>
    </Card>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import Card from 'primevue/card';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputMask from 'primevue/inputmask';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';

import ConfiguracaoSistema from '@/service/configuracaoSistema.js';

const FIELD_KEYS = Object.freeze([
    'banco',
    'agencia',
    'agencia_dv',
    'conta',
    'conta_dv',
    'convenio',
    'codigo_cedente',
    'cnpj',
    'sequencial_inicial',
    'sequencial_atual',
]);

const FIELD_LABELS = Object.freeze({
    banco: 'Banco',
    agencia: 'Agência',
    agencia_dv: 'DV da agência',
    conta: 'Conta',
    conta_dv: 'DV da conta',
    convenio: 'Número do convênio',
    codigo_cedente: 'Código do cedente',
    cnpj: 'CNPJ',
    sequencial_inicial: 'Sequencial inicial',
    sequencial_atual: 'Sequencial atual',
});

const EMPTY_FORM = Object.freeze(
    FIELD_KEYS.reduce((acc, key) => ({ ...acc, [key]: '' }), {}),
);

const BANCO_LABEL = '237 — Bradesco';

const toast = useToast();

// IDs únicos por instância — Vue 3.4 ainda não tem useId nativo
let __dbIdSeq = 0;
const localId = () => `db-${++__dbIdSeq}-${Math.random().toString(36).slice(2, 8)}`;
const agenciaId = localId();
const agenciaDvId = localId();
const contaId = localId();
const contaDvId = localId();
const convenioId = localId();
const codigoCedenteId = localId();
const cnpjId = localId();
const seqInicialId = localId();
const seqAtualId = localId();

const loading = ref(true);
const saving = ref(false);
const form = reactive({ ...EMPTY_FORM });
const errors = reactive({});
const baseline = ref({ ...EMPTY_FORM });
const statusCompleto = ref(false);
const camposFaltantes = ref([]);

const bancoLabel = computed(() => BANCO_LABEL);

const camposFaltantesLabels = computed(() =>
    camposFaltantes.value.map((k) => FIELD_LABELS[k] ?? k),
);

const isDirty = computed(() =>
    FIELD_KEYS.some((key) => (form[key] ?? '') !== (baseline.value[key] ?? '')),
);

function onNumericInput(field, event, maxLen) {
    const raw = event?.target?.value ?? '';
    const clean = raw.replace(/\D+/g, '').slice(0, maxLen);
    form[field] = clean;
    if (errors[field]) {
        delete errors[field];
    }
}

function onAlphaNumDvInput(field, event) {
    const raw = event?.target?.value ?? '';
    // DV pode ser 0-9 ou letra (P para conta Bradesco quando aplicável)
    const clean = raw.replace(/[^0-9A-Za-z]/g, '').slice(0, 1).toUpperCase();
    form[field] = clean;
    if (errors[field]) {
        delete errors[field];
    }
}

function applyPayload(payload) {
    const safe = payload ?? {};
    FIELD_KEYS.forEach((key) => {
        const value = safe[key];
        form[key] = value == null ? '' : String(value);
    });
    // Banco sempre exibido como label fixo, mas mantém o valor recebido por integridade
    if (!form.banco) {
        form.banco = '237';
    }
    baseline.value = { ...form };
    Object.keys(errors).forEach((k) => delete errors[k]);
}

function applyStatus(payload) {
    statusCompleto.value = !!payload?.completo;
    camposFaltantes.value = Array.isArray(payload?.faltando) ? payload.faltando : [];
}

function resetForm() {
    FIELD_KEYS.forEach((key) => {
        form[key] = baseline.value[key] ?? '';
    });
    Object.keys(errors).forEach((k) => delete errors[k]);
}

function validateClient() {
    const next = {};
    const cnpjDigits = (form.cnpj || '').replace(/\D+/g, '');

    if (!form.agencia) next.agencia = 'Informe a agência.';
    if (!form.agencia_dv) next.agencia_dv = 'Informe o DV da agência.';
    if (!form.conta) next.conta = 'Informe a conta.';
    if (!form.conta_dv) next.conta_dv = 'Informe o DV da conta.';
    if (!form.convenio) next.convenio = 'Informe o número do convênio.';
    if (!form.codigo_cedente) next.codigo_cedente = 'Informe o código do cedente.';
    if (cnpjDigits.length !== 14) next.cnpj = 'CNPJ deve ter 14 dígitos.';
    if (!form.sequencial_inicial) {
        next.sequencial_inicial = 'Informe o sequencial inicial.';
    } else if (!/^\d+$/.test(form.sequencial_inicial)) {
        next.sequencial_inicial = 'Use apenas dígitos.';
    }

    Object.keys(errors).forEach((k) => delete errors[k]);
    Object.assign(errors, next);
    return Object.keys(next).length === 0;
}

function buildPayload() {
    return {
        banco: form.banco || '237',
        agencia: form.agencia,
        agencia_dv: form.agencia_dv,
        conta: form.conta,
        conta_dv: form.conta_dv,
        convenio: form.convenio,
        codigo_cedente: form.codigo_cedente,
        // Backend valida `cnpj` com regex /^\d{14}$/ (UpdateDadosBancariosRequest
        // do Cursor + DadosBancariosService::cnpjDvValido). InputMask devolve
        // string com pontos/barra/traço (`04.252.011/0001-10`) que estoura a
        // regex. Strippa não-dígitos antes do PUT — máscara é só UX.
        cnpj: (form.cnpj || '').replace(/\D+/g, ''),
        sequencial_inicial: form.sequencial_inicial,
        // sequencial_atual é gerenciado pelo backend; enviamos o valor atual sem alterar
        sequencial_atual: form.sequencial_atual,
    };
}

async function loadAll() {
    loading.value = true;
    try {
        const [resDados, resStatus] = await Promise.all([
            ConfiguracaoSistema.getDadosBancarios(),
            ConfiguracaoSistema.getDadosBancariosStatus(),
        ]);
        applyPayload(resDados?.data ?? {});
        applyStatus(resStatus?.data ?? {});
    } catch (e) {
        toast.add({
            severity: 'error',
            summary: 'Erro',
            detail:
                e.response?.data?.message ||
                e.message ||
                'Não foi possível carregar os dados bancários.',
            life: 5000,
        });
    } finally {
        loading.value = false;
    }
}

async function refreshStatus() {
    try {
        const { data } = await ConfiguracaoSistema.getDadosBancariosStatus();
        applyStatus(data ?? {});
    } catch (e) {
        // Falha silenciosa: o status é informativo; o save em si já foi reportado.
    }
}

// Traduz mensagens cruas do validator do Laravel pra PT-BR.
// Backend devolve coisas como "The cnpj field format is invalid."
// ou "The agencia field is required." — heurística por sufixo + fallback.
function traduzErroLaravel(message) {
    if (typeof message !== 'string' || !message.trim()) {
        return 'Campo inválido.';
    }
    // Se já está em PT (ex.: mensagens vindas via `erros` do backend), preserva.
    if (!/\bThe\b|\bfield\b|\bmust\b/i.test(message)) {
        return message;
    }
    const lower = message.toLowerCase();
    if (lower.includes('required')) return 'Campo obrigatório.';
    if (lower.includes('format') || lower.includes('regex') || lower.includes('invalid'))
        return 'Formato inválido.';
    if (lower.includes('max') || lower.includes('greater') || lower.includes('large'))
        return 'Valor muito longo.';
    if (lower.includes('min') || lower.includes('less') || lower.includes('short'))
        return 'Valor muito curto.';
    if (lower.includes('unique') || lower.includes('already'))
        return 'Valor já cadastrado.';
    if (lower.includes('numeric') || lower.includes('integer') || lower.includes('digits'))
        return 'Use apenas dígitos.';
    return 'Campo inválido.';
}

function applyServerErrors(serverErrors) {
    if (!serverErrors || typeof serverErrors !== 'object') {
        return null;
    }
    let firstMsg = null;
    Object.entries(serverErrors).forEach(([field, msgs]) => {
        const raw = Array.isArray(msgs) ? msgs[0] : msgs;
        const translated = traduzErroLaravel(raw);
        if (FIELD_KEYS.includes(field)) {
            errors[field] = translated;
        }
        if (!firstMsg) {
            firstMsg = translated;
        }
    });
    return firstMsg;
}

async function onSubmit() {
    if (!validateClient()) {
        toast.add({
            severity: 'warn',
            summary: 'Verifique os campos',
            detail: 'Há campos obrigatórios ou inválidos no formulário.',
            life: 4000,
        });
        return;
    }

    saving.value = true;
    try {
        await ConfiguracaoSistema.updateDadosBancarios(buildPayload());
        baseline.value = { ...form };
        toast.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Dados bancários atualizados.',
            life: 4000,
        });
        await refreshStatus();
    } catch (e) {
        const data = e.response?.data;
        // Backend pode devolver `errors` (Laravel default, EN) ou `erros` (PT, em endpoints locais).
        const validationFirst = applyServerErrors(data?.errors ?? data?.erros);
        toast.add({
            severity: 'error',
            summary: 'Erro ao salvar',
            detail: validationFirst || data?.message || e.message || 'Falha ao salvar.',
            life: 6000,
        });
    } finally {
        saving.value = false;
    }
}

onMounted(loadAll);
</script>
